'use client';

import { useCallback, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useReportWebVitals } from 'next/web-vitals';
import { trackWebPagePerformanceMeasured, WebVitalMetric } from './eventHandlerTracking';

/**
 * Drops a report once the visitor has left its measured route: web-vitals can report late, and the SDK stamps events
 * with the page current at send time.
 */
export function WebVitalsReporter() {
  const pathname = usePathname();
  const measuredPathname = useRef<string | null>(null);
  const hasLeftMeasuredPage = useRef(false);

  // Read from window.location, not usePathname, because that is the page the SDK will stamp on the event.
  useEffect(() => {
    if (measuredPathname.current === null) {
      measuredPathname.current = window.location.pathname;
    } else if (window.location.pathname !== measuredPathname.current) {
      // Sticky: without the Soft Navigations API, a hard-navigation metric keeps accumulating across routes, so
      // returning to the measured route still carries shifts and interactions from the detour.
      hasLeftMeasuredPage.current = true;
    }
  }, [pathname]);

  // A back-forward cache restore starts new measurements on the restored page. This listener is registered before
  // useReportWebVitals and, like web-vitals, in the capture phase, so it runs before web-vitals reports the restore.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      measuredPathname.current = window.location.pathname;
      hasLeftMeasuredPage.current = false;
    };

    window.addEventListener('pageshow', handlePageShow, { capture: true });
    return () => window.removeEventListener('pageshow', handlePageShow, { capture: true });
  }, []);

  // Stable, because useReportWebVitals registers the web-vitals listeners again whenever its callback changes.
  const reportWebVital = useCallback((metric: WebVitalMetric) => {
    if (!isMeasuredRouteCurrent(metric, measuredPathname.current, hasLeftMeasuredPage.current)) return;
    trackWebPagePerformanceMeasured(metric);
  }, []);

  useReportWebVitals(reportWebVital);

  return null;
}

function isMeasuredRouteCurrent(
  metric: WebVitalMetric,
  measuredPathname: string | null,
  hasLeftMeasuredPage: boolean,
): boolean {
  // Each soft navigation starts fresh measurements, and web-vitals records the route they belong to.
  if (metric.navigationType === 'soft-navigation') {
    return (
      metric.navigationURL !== undefined &&
      new URL(metric.navigationURL, window.location.href).pathname === window.location.pathname
    );
  }

  // Not navigationURL: without the Soft Navigations API it keeps the landing URL after a back-forward cache restore.
  return !hasLeftMeasuredPage && window.location.pathname === measuredPathname;
}
